<!-- @format -->

<template>
    <div class="gst-event-totals-container d-flex u-width-100">
        <div class="d-flex flex-column flex-grow-1">
            <h4>{{ title }}:</h4>
            <p
                v-if="totalPrice && !isOpenInventoryFlow && !isManageBookingFlow"
                class="mb-0 text-lowercase">
                {{ selectedItemsWithQuantity }}
            </p>
        </div>
        <div
            v-if="totalPrice"
            class="d-flex flex-column flex-grow-1 text-right">
            <h4>
                <template v-if="redeemPointsToggle">
                    {{
                        $t('shared:common.pointWithCount', {
                            count: selectedHotel.redeemPoints,
                            formattedCount: formattedPoints,
                        })
                    }}
                </template>
                <template v-else>
                    {{ totalPrice | currencyFilter(currency) }}
                </template>
            </h4>
            <p
                v-if="!isManageBookingFlow"
                class="mb-0">
                <span v-if="!isOpenInventoryFlow">{{
                    pricePerPerson | currencyFilter(currency)
                }}</span>
                <span class="text-lowercase">
                    <template v-if="redeemPointsToggle && filteredFeesByIncludedType.length">
                        + {{ `${$t('_common:terms.payAtProperty')}: ${excludedPropertyFees}` }}
                    </template>
                    <template v-if="!redeemPointsToggle">
                        {{
                            isOpenInventoryFlow
                                ? $t('_common:terms.includesTaxesAndFeesAbbr')
                                : $t('_common:terms.perPerson')
                        }}
                    </template>
                </span>
            </p>
        </div>
        <div
            v-else
            class="d-flex flex-column flex-grow-1 text-right gst-event-totals-container__no-price">
            <h4>{{ noSelectionPrice }}</h4>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import roomRateFee from '@core/utils/constants/roomRateFee';
import { feesFilteredByIncludedType } from '@core/utils/feeUtils';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import currency from '@tenant/app/filters/currency';

export default {
    name: 'EventTotalsContainer',
    mixins: [EventFlowMixin],
    props: {
        title: {
            type: String,
            default: null,
        },
        bundleProducts: {
            type: Object,
            default: null,
        },
        event: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
        }),
        selectedHotel() {
            if (this.bundleProducts.hasHotelReservation()) {
                return this.bundleProducts.hotelReservation.hotel;
            }
            return null;
        },
        currency() {
            if (this.selectedHotel) {
                return this.selectedHotel.selectedRoom
                    ? this.selectedHotel.selectedRoom.selectedRate.currency
                    : this.event.currency;
            }

            return this.event.currency;
        },
        adultsCount() {
            return (
                (this.selectedHotel &&
                    this.selectedHotel.selectedRoom.accommodation.guests.adultsCount) ||
                0
            );
        },
        totalPrice() {
            return this.bundleProducts.getTotals().basePrice;
        },
        pricePerPerson() {
            return this.totalPrice / this.adultsCount;
        },
        noSelectionPrice() {
            const tempPrice = this.$options.filters.currencyFilter(0, this.currency);
            const price = tempPrice.replace('0.00', '-');

            return this.isManageBookingFlow ? tempPrice : price;
        },
        selectedItemsWithQuantity() {
            if (this.bundleProducts.hasHotelReservation()) {
                return this.$t('_common:terms.nightWithCount', {
                    count: this.selectedHotel.selectedRoom.accommodation.nights,
                });
            }

            return '';
        },
        filteredFeesByIncludedType() {
            const fees = this.selectedHotel.selectedRoom.selectedRate.fees;
            return feesFilteredByIncludedType(fees, roomRateFee.TYPES_INCLUDED.PAY_LATER);
        },
        excludedPropertyFees() {
            return currency(
                this.filteredFeesByIncludedType.reduce((acc, item) => {
                    return acc + item.fee.amount;
                }, 0),
                this.currency,
                { digits: 2 },
                false
            );
        },
        formattedPoints() {
            return this.$options.filters.bonusPoints(
                this.selectedHotel.selectedRoom.selectedRate.redeemPoints
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@scssVariables';
@import '@scssMixins';

.gst-event-totals-container {
    padding: theme-spacing(4);
    background: theme-color-hex('quinary');
    color: theme-color('primary');
    z-index: 1;

    h4 {
        line-height: line-height('xxxxl');
        font-size: font-size('l');
        font-weight: font-weight('bold');
    }

    p {
        line-height: line-height('m');
        font-size: font-size('xxs');
        font-weight: font-weight('regular');
    }

    .gst-event-totals-container__no-price {
        min-height: 39px;
    }
}

@include mobile-only {
    .gst-event-totals-container {
        padding-top: theme-spacing(2);
        padding-bottom: theme-spacing(2);
    }
}
</style>
